import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal, computed, SecurityContext, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { DomSanitizer } from '@angular/platform-browser';
import { ConvenioSeleccionado } from 'src/app/domain/model/convenio/convenio';
import {
  EncabezadoFactura,
  PeriodoFacturacion
} from 'src/app/domain/model/servicios-entregados/factura-encabezado.model';
import { FacturaEncabezadoRepository } from 'src/app/domain/service/servicios-entregados/factura-encabezado-repository';
import { OficinaRepository } from 'src/app/domain/service/oficina/oficina-repository';
import { Oficina } from 'src/app/domain/model/oficina/oficina';
import { sanitizeText, sanitizeTextarea } from 'src/app/common/input-utils';
import { BuscadorConvenioDialogComponent } from '../shared/buscadores/buscador-convenio-dialog.component';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';

interface OpcionServicioFacturacion {
  label: string;
  value: number;
}

interface OpcionMes {
  label: string;
  value: number;
}

@Component({
  selector: 'app-nuevo-encabezado-factura',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    TextareaModule,
    ProgressSpinnerModule,
    ToastModule,
    TooltipModule,
    BuscadorConvenioDialogComponent
  ],
  providers: [MessageService],
  template: `
    <div class="p-4 md:p-6 max-w-6xl mx-auto">
      <p-card>
        <div class="px-2 md:px-4 pb-4">
          @if (loading()) {
            <div class="flex justify-center py-6">
              <p-progressSpinner strokeWidth="4" [style]="{ width: '48px', height: '48px' }"></p-progressSpinner>
            </div>
          }

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Nombre Encabezado *</label>
              <input
                pInputText
                [(ngModel)]="form.cdfactura"
                maxlength="12"
                class="w-full"
                (input)="onNombreEncabezadoInput($event)"
                (blur)="onFacturaChange()" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Oficina *</label>
              <p-dropdown
                [options]="oficinas()"
                optionLabel="dsoficina"
                optionValue="nmconsecutivo"
                [(ngModel)]="form.nmconsecutivoOficina"
                placeholder="Seleccione"
                [filter]="true"
                filterBy="dsoficina,cdoficina"
                class="w-full"
                appendTo="body"
                (onChange)="onOficinaChange()">
              </p-dropdown>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Servicio *</label>
              <p-dropdown
                [options]="tiposServicio"
                [(ngModel)]="form.cdservicioFacturacion"
                optionLabel="label"
                optionValue="value"
                placeholder="Seleccione"
                class="w-full"
                appendTo="body"
                (onChange)="onServicioChange()">
              </p-dropdown>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Convenio *</label>
              <div class="flex gap-2">
                <input pInputText [ngModel]="convenioSanitizado()" readonly class="w-full" />
                <button
                  pButton
                  icon="pi pi-search"
                  class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  pTooltip="Buscar convenio"
                  severity="primary"
                  size="small"
                  (click)="abrirBusquedaConvenio()"
                >
                </button>
                <button
                  pButton
                  icon="pi pi-times"
                  class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="danger"
                  size="small"
                  pTooltip="Limpiar convenio"
                  (click)="limpiarConvenio()"
                  [disabled]="!convenioSeleccionado()"
                >
                </button>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Mes *</label>
              <p-dropdown
                [options]="meses"
                [(ngModel)]="form.mes"
                optionLabel="label"
                optionValue="value"
                placeholder="Seleccione"
                class="w-full"
                appendTo="body"
                (onChange)="onMesChange()">
              </p-dropdown>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Año *</label>
              <input
                pInputText
                [(ngModel)]="anioTexto"
                maxlength="4"
                inputmode="numeric"
                class="w-full"
                (input)="onAnioInput($event)"
                (blur)="onAnioChange()" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Fecha inicial</label>
              <input pInputText [ngModel]="periodo()?.fedesde || ''" readonly class="w-full" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Fecha final</label>
              <input pInputText [ngModel]="periodo()?.fehasta || ''" readonly class="w-full" />
            </div>

            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
              <textarea
                pTextarea
                [(ngModel)]="form.dsobservaciones"
                rows="4"
                maxlength="200"
                (input)="onObservacionesInput($event)"
                class="w-full"></textarea>
            </div>
          </div>

          <div class="flex flex-wrap justify-end gap-3 mt-6 w-full">
            <p-button label="Limpiar" icon="pi pi-eraser" [outlined]="true" (onClick)="limpiar()"></p-button>
            <p-button label="Guardar" icon="pi pi-check" (onClick)="guardar()" [disabled]="loading()"></p-button>
          </div>
        </div>
      </p-card>
    </div>

    @if (dialogConvenioVisible()) {
      <app-busqueda-convenio-dialog
        [visible]="true"
        (visibleChange)="dialogConvenioVisible.set($event)"
        (convenioSeleccionado)="onConvenioSeleccionado($event)">
      </app-busqueda-convenio-dialog>
    }

    <p-toast></p-toast>
  `
})
export class NuevoEncabezadoFacturaComponent implements OnInit {
  @Output() encabezadoCreado = new EventEmitter<EncabezadoFactura>();

  private readonly oficinaRepository = inject(OficinaRepository);
  private readonly facturaEncabezadoRepository = inject(FacturaEncabezadoRepository);
  private readonly messageService = inject(MessageService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly usuarioState = inject(UsuarioStateService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly tiposServicio: OpcionServicioFacturacion[] = [
    { label: 'Servicios PDF', value: 1 },
    { label: 'Otros servicios', value: 2 },
    { label: 'Servicios empresariales', value: 3 }
  ];

  readonly meses: OpcionMes[] = [
    { label: 'Enero', value: 1 },
    { label: 'Febrero', value: 2 },
    { label: 'Marzo', value: 3 },
    { label: 'Abril', value: 4 },
    { label: 'Mayo', value: 5 },
    { label: 'Junio', value: 6 },
    { label: 'Julio', value: 7 },
    { label: 'Agosto', value: 8 },
    { label: 'Septiembre', value: 9 },
    { label: 'Octubre', value: 10 },
    { label: 'Noviembre', value: 11 },
    { label: 'Diciembre', value: 12 }
  ];

  readonly oficinas = signal<Oficina[]>([]);
  readonly loading = signal(false);
  readonly dialogConvenioVisible = signal(false);
  readonly convenioSeleccionado = signal<ConvenioSeleccionado | null>(null);
  
  // CWE-79 Fix: Signal computed para sanitizar el convenio (XSS prevention)
  readonly convenioSanitizado = computed(() => {
    const dsconvenio = this.convenioSeleccionado()?.dsconvenio;
    if (!dsconvenio) return '';
    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, dsconvenio);
    return sanitized || '';
  });
  
  readonly periodo = signal<PeriodoFacturacion | null>(null);
  readonly nombreExiste = signal(false);
  readonly periodoValido = signal(false);

  form: Partial<EncabezadoFactura> = {};
  anioTexto = '';

  async ngOnInit(): Promise<void> {
    try {
      // Cargar oficinas y usuario en paralelo
      await Promise.all([
        this.cargarOficinas(),
        this.usuarioState.cargar()
      ]);
      
      // Pre-seleccionar la oficina del usuario autenticado SSO
      const usuario = this.usuarioState.usuario();
      if (usuario?.nmconsecOficina) {
        this.form.nmconsecutivoOficina = usuario.nmconsecOficina;
        // Forzar detección de cambios para que ngModel se actualice
        this.cdr.markForCheck();
      }
    } catch (error) {
      this.mostrarMensaje('Error cargando datos iniciales', 'error');
    }
  }

  async onFacturaChange(): Promise<void> {
    const nombre = this.form.cdfactura?.trim();
    if (!nombre) {
      return;
    }

    this.form.cdfactura = nombre.toUpperCase();
    try {
      const existe = await this.facturaEncabezadoRepository.existeNombre(this.form.cdfactura);
      this.nombreExiste.set(existe);
      if (existe) {
        this.mostrarMensaje('El nombre del encabezado ya se encuentra registrado', 'warning');
        this.form.cdfactura = '';
      }
    } catch (error) {
      this.mostrarMensaje('Error validando el nombre del encabezado', 'error');
    }
  }

  onNombreEncabezadoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valorSanitizado = sanitizeText(input.value);
    this.form.cdfactura = valorSanitizado;
    input.value = valorSanitizado;
  }

  onObservacionesInput(event: Event): void {
    const input = event.target as HTMLTextAreaElement;
    const valorSanitizado = sanitizeTextarea(input.value);
    this.form.dsobservaciones = valorSanitizado;
    input.value = valorSanitizado;
  }

  onOficinaChange(): void {
    this.resetPeriodoFields();
  }

  onServicioChange(): void {
    this.resetPeriodoFields();
  }

  async onMesChange(): Promise<void> {
    await this.cargarPeriodoDesdeSeleccion();
  }

  onAnioInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Filtrar solo dígitos numéricos
    const soloNumeros = input.value.replace(/[^0-9]/g, '');
    this.anioTexto = soloNumeros;
    input.value = soloNumeros;
  }

  async onAnioChange(): Promise<void> {
    this.anioTexto = this.anioTexto.trim();
    await this.cargarPeriodoDesdeSeleccion();
  }

  abrirBusquedaConvenio(): void {
    this.dialogConvenioVisible.set(true);
  }

  limpiarConvenio(): void {
    this.convenioSeleccionado.set(null);
    this.form.nmconsecutivoConvenio = undefined;
    this.periodoValido.set(false);
    this.periodo.set(null);
  }

  async onConvenioSeleccionado(convenio: ConvenioSeleccionado): Promise<void> {
    // CWE-434 Fix: Validar datos de entrada antes de procesar
    const validacionError = this.validarConvenioSeleccionado(convenio);
    if (validacionError) {
      this.mostrarMensaje(validacionError, 'error');
      return;
    }

    this.convenioSeleccionado.set(convenio);
    this.form.nmconsecutivoConvenio = convenio.nmconsecutivo;
    this.dialogConvenioVisible.set(false);

    if (this.puedeValidarPeriodo()) {
      await this.validarPeriodo();
    }
  }

  /**
   * Valida que el convenio seleccionado cumple con estructura y límites requeridos.
   * Retorna null si es válido, o un mensaje de error si es inválido.
   * CWE-434: Validación de datos de entrada no confiables
   */
  private validarConvenioSeleccionado(convenio: ConvenioSeleccionado): string | null {
    // Validación 1: Objeto no null/undefined
    if (!convenio) {
      return 'Convenio seleccionado no es válido.';
    }

    // Validación 2: Propiedades requeridas presentes
    if (convenio.nmconsecutivo === null || convenio.nmconsecutivo === undefined) {
      return 'El convenio no tiene identificador válido.';
    }

    if (!convenio.dsconvenio || convenio.dsconvenio.trim() === '') {
      return 'El convenio no puede estar vacío.';
    }

    // Validación 3: nmconsecutivo es número entero positivo
    if (!Number.isInteger(convenio.nmconsecutivo) || convenio.nmconsecutivo <= 0) {
      return 'El identificador del convenio debe ser un número positivo.';
    }

    // Validación 4: dsconvenio no excede longitud máxima (previene DoS)
    if (convenio.dsconvenio.length > 500) {
      return 'El nombre del convenio es demasiado largo.';
    }

    // Validación 5: dsconvenio no es solo espacios en blanco
    if (convenio.dsconvenio.trim().length === 0) {
      return 'El nombre del convenio no puede estar vacío.';
    }

    return null; // Válido
  }

  limpiar(): void {
    this.form = {};
    this.anioTexto = '';
    this.periodo.set(null);
    this.convenioSeleccionado.set(null);
    this.nombreExiste.set(false);
    this.periodoValido.set(false);
    this.limpiarMensaje();
  }

  async guardar(): Promise<void> {
    const validationMessage = this.validarFormulario();
    if (validationMessage) {
      this.mostrarMensaje(validationMessage, 'warning');
      return;
    }

    await this.onFacturaChange();
    if (this.nombreExiste()) {
      return;
    }

    await this.validarPeriodo();
    if (!this.periodoValido()) {
      return;
    }

    this.loading.set(true);
    try {
      const payload: EncabezadoFactura = {
        cdfactura: this.form.cdfactura!,
        nmconsecutivoOficina: this.form.nmconsecutivoOficina!,
        nmconsecutivoConvenio: this.form.nmconsecutivoConvenio!,
        cdservicioFacturacion: this.form.cdservicioFacturacion!,
        anio: Number(this.anioTexto),
        mes: this.form.mes!,
        dsobservaciones: this.form.dsobservaciones || ''
      };

      const creado = await this.facturaEncabezadoRepository.crearEncabezado(payload);
      if (creado) {
        this.encabezadoCreado.emit(creado);
        this.limpiar();
      }
      this.mostrarMensaje('Encabezado creado correctamente', 'success');
    } catch (error) {
      this.mostrarMensaje(
        'No fue posible guardar el encabezado. Intente nuevamente o contacte soporte.',
        'error'
      );
    } finally {
      this.loading.set(false);
    }
  }

  private async cargarOficinas(): Promise<void> {
    this.loading.set(true);
    try {
      const oficinas = await this.oficinaRepository.obtenerOficinas();
      this.oficinas.set(oficinas || []);
    } catch (error) {
      this.mostrarMensaje('Error al cargar oficinas', 'error');
    } finally {
      this.loading.set(false);
    }
  }

  private async cargarPeriodoDesdeSeleccion(): Promise<void> {
    if (!this.form.nmconsecutivoOficina || !this.form.cdservicioFacturacion) {
      this.mostrarMensaje('No se ha seleccionado la oficina o el tipo de servicio', 'warning');
      return;
    }

    if (!this.form.mes || !this.esAnioValido()) {
      return;
    }

    this.loading.set(true);
    try {
      const periodo = await this.facturaEncabezadoRepository.obtenerPeriodo(
        Number(this.anioTexto),
        this.form.mes,
        this.form.nmconsecutivoOficina
      );
      this.periodo.set(periodo);
      this.limpiarMensaje();
      if (this.puedeValidarPeriodo()) {
        await this.validarPeriodo();
      }
    } catch (error) {
      this.periodo.set(null);
      this.periodoValido.set(false);
      this.mostrarMensaje('No existe periodo de facturación configurado para la selección realizada', 'warning');
    } finally {
      this.loading.set(false);
    }
  }

  private async validarPeriodo(): Promise<void> {
    if (!this.puedeValidarPeriodo()) {
      return;
    }

    this.loading.set(true);
    try {
      const resultado = await this.facturaEncabezadoRepository.validarPeriodo({
        nmconsecutivoOficina: this.form.nmconsecutivoOficina!,
        nmconsecutivoConvenio: this.form.nmconsecutivoConvenio!,
        cdservicioFacturacion: this.form.cdservicioFacturacion!,
        anio: Number(this.anioTexto),
        mes: this.form.mes!
      });

      this.periodoValido.set(resultado.valido);
      if (resultado.periodo) {
        this.periodo.set(resultado.periodo);
      }
      if (resultado.valido) {
        this.limpiarMensaje();
      } else {
        this.mostrarMensaje(resultado.mensaje || 'Ya existe una factura para el período seleccionado', 'warning');
      }
    } catch (error) {
      this.periodoValido.set(false);
      this.mostrarMensaje('Error validando el período de facturación', 'error');
    } finally {
      this.loading.set(false);
    }
  }

  private validarFormulario(): string | null {
    if (!this.form.cdfactura?.trim()) {
      return 'Nombre Encabezado es obligatorio';
    }
    if (!this.form.nmconsecutivoOficina) {
      return 'Oficina es obligatoria';
    }
    if (!this.form.cdservicioFacturacion) {
      return 'Servicio es obligatorio';
    }
    if (!this.form.nmconsecutivoConvenio) {
      return 'Convenio es obligatorio';
    }
    if (!this.form.mes) {
      return 'Mes es obligatorio';
    }
    if (!this.esAnioValido()) {
      return 'Año debe ser numérico de 4 dígitos';
    }
    if (!this.periodo()) {
      return 'Debe existir un período de facturación válido';
    }
    return null;
  }

  private esAnioValido(): boolean {
    return /^\d{4}$/.test(this.anioTexto.trim());
  }

  private puedeValidarPeriodo(): boolean {
    return !!this.form.nmconsecutivoOficina
      && !!this.form.cdservicioFacturacion
      && !!this.form.nmconsecutivoConvenio
      && !!this.form.mes
      && this.esAnioValido();
  }

  private resetPeriodoFields(): void {
    this.form.mes = undefined;
    this.anioTexto = '';
    this.periodo.set(null);
    this.periodoValido.set(false);
  }

  private mostrarMensaje(mensaje: string, tipo: 'warning' | 'error' | 'success'): void {
    const severityMap = {
      'warning': 'warn',
      'error': 'error',
      'success': 'success'
    };

    const summaryMap = {
      'warning': 'Advertencia',
      'error': 'Error',
      'success': 'Éxito'
    };

    this.messageService.add({
      severity: severityMap[tipo] as any,
      summary: summaryMap[tipo],
      detail: mensaje,
      life: 3000
    });
  }

  private limpiarMensaje(): void {
    // No es necesario implementar con p-toast
  }
}
